import React, { useContext, useEffect, useRef, useState } from 'react';
import { Observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import { Toolbar, Item, Mention } from 'devextreme-react/html-editor';
import Quill from 'devextreme-quill';
import { toast } from 'react-toastify';

import BodyEnd from '../../_shared/BodyEnd';
import ConfirmModal from '../../_shared/ConfirmModalComponent';
import HtmlEditor from '../../_shared/HtmlEditor';

import CommunicationTemplateUpdateStore from '../../../../stores/CommunicationTemplateUpdateStore';
import QuickDrawerStore from '../../../../stores/QuickDrawerStore';

import * as ErrorMessages from '../../../../constants/errorMessages';
import * as fn from '../../../../utilities/_functions';
import * as dxm from '../../../../utilities/dx/mentionHelper';
import api from '../../../../api';

function EmailPartial(props) {
    const isMounted = useRef(true);
    const validateRef = useRef(null);
    const emailBodyElementRef = useRef(null);
    const emailBody = useRef();
    const confirmModalRef = useRef();
    const hasUnsavedChanges = useRef(false);
    const communicationTemplateUpdate = useContext(CommunicationTemplateUpdateStore);
    const quickDrawer = useContext(QuickDrawerStore);
    const [name, setName] = useState((communicationTemplateUpdate.selectedPartial ? communicationTemplateUpdate.selectedPartial.name : ''));

    useEffect(() => {
        if (communicationTemplateUpdate.selectedPartial) {
            emailBody.current = toJS(communicationTemplateUpdate.selectedPartial.body);
        }

        return () => {
            isMounted.current = false;
        }
    }, []) // eslint-disable-line

    const handleClose = event => {
        if (fn.isFunction(props.onClose)) {
            if (hasUnsavedChanges.current) {
                if (window.confirm(ErrorMessages.DISCARD_CHANGES)) {
                    props.onClose();
                }
            } else {
                props.onClose();
            }
        }
    }

    const handleUploadInlineImage = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';

        input.onchange = (e) => {
            const file = e.target.files[0];
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onloadend = () => {
                // reader.result contains the Base64 string (e.g., "data:image/png;base64,iVBOR...")
                const base64Data = reader.result;

                // Send as JSON
                api.Files.upload({
                    fileName: file.name,
                    size: file.size,
                    base64: base64Data,
                })
                    .then(({ data }) => {
                        const editorInstance = emailBodyElementRef.current.getInstance();
                        const selection = editorInstance.getSelection();
                        editorInstance.insertEmbed(selection ? selection.index : 0, 'image', data.url);
                    });
            };
        };

        input.click();
    };

    const handleNameChange = event => {
        const { value } = event.target;
        setName(value);
        hasUnsavedChanges.current = true;
    }

    const handleHtmlEditorContentReady = (event) => {
        if (event && event.component) {
            const instance = event && event.component.getQuillInstance();

            if (instance) {
                instance.root.addEventListener('dblclick', (e) => { handleHtmlEditorMentionDoubleClick(e, instance) });
            }
        }
    }

    const handleHtmlEditorMentionDoubleClick = (event, quillInstance) => {
        const mentionElement = event.target.closest('.dx-mention');

        if (mentionElement) {
            const selectedItem = mentionElement.dataset.id;
            const variableId = selectedItem.replace(/\[[^\]]*\]/g, '').trim();
            const selection = quillInstance.getSelection();
            const variables = toJS(communicationTemplateUpdate.selectedTemplate.variables);
            const variable = variables.reduce((result, item) => {
                if (!result) {
                    result = item.variables.find(v => v.formats.some(f => f.variable === variableId))
                }
                return result;
            }, null);
            const extraProps = {
                quillInstance: quillInstance,
                variable: variable,
                selectedItem: selectedItem,
                selection: selection,
                mentionElement: mentionElement,
                supportHtml: true,
            };

            if (variable) {
                quickDrawer.activateQuickDrawer('communicationTemplate', 'variable', extraProps, handleVariableChangeSuccess, handleVariableChangeCancel);
            }
        }
    };

    const handleVariableChangeSuccess = (result) => {
        const quillInstance = result.quillInstance;
        const blot = Quill.find(result.mentionElement);

        if (blot) {
            const index = quillInstance.getIndex(blot);
            const length = blot.length();
            const newMention = { id: result.selectedId, value: result.selectedId, marker: '#' };

            quillInstance.deleteText(index, length);
            quillInstance.insertEmbed(index, 'mention', newMention);
        }

        hasUnsavedChanges.current = true;
    }

    const handleVariableChangeCancel = (event) => { }

    const handleEmailBodyChange = (e) => {
        const html = e.value;
        const bodyHtml = (html === '<p></p>') || (html === '<p><br></p>') ? '' : html;
        emailBody.current = bodyHtml;
        hasUnsavedChanges.current = true;
    }

    const handleDeletePartial = (event) => {
        confirmModalRef.current.show({
            icon: <i className='fal fa-trash-alt text-danger mr-2'></i>,
            message: <>Continue to delete <span className='fw-500'>&nbsp;{(communicationTemplateUpdate.selectedPartial ? communicationTemplateUpdate.selectedPartial.name : '')}</span>? </>,
            option1ClassName: 'btn btn-danger shadow-0 bootbox-accept',
            option1Text: 'Delete',
            cancelText: 'Cancel',
            onOption1Click: handleDeletePartialConfirm,
            onCancel: handleDeletePartialCancel
        })
    }

    const handleDeletePartialConfirm = (event) => {
        const partialKey = communicationTemplateUpdate.selectedPartialVariable ? communicationTemplateUpdate.selectedPartialVariable.value : '';

        communicationTemplateUpdate.deletePartial(true)
            .then(() => {
                if (isMounted.current) {
                    confirmModalRef.current.close();
                    toast.dark(() => <p>{partialKey} deleted.</p>);

                    if (fn.isFunction(props.onSaved)) {
                        props.onSaved(event, { data: { id: null } });
                    }
                }
            })
    }

    const handleDeletePartialCancel = (event) => {
        confirmModalRef.current.close();
    }

    const handleSubmit = event => {
        event.preventDefault();

        if (fn.validateForm(validateRef.current)) {
            const partialKey = communicationTemplateUpdate.selectedPartialVariable ? communicationTemplateUpdate.selectedPartialVariable.value : '';
            const body = dxm.mentionToVariable(emailBody.current, '{{$1}}');

            communicationTemplateUpdate.selectedPartial.name = name;
            communicationTemplateUpdate.selectedPartial.body = body;

            communicationTemplateUpdate.savePartial(true)
                .then((data) => {
                    if (isMounted.current) {
                        toast.dark(() => <p>{partialKey} updated.</p>);

                        if (fn.isFunction(props.onSaved)) {
                            props.onSaved(event, { data: data });
                        }
                    }
                })
        }
    }

    const renderTitle = () => {
        return <div className='popup-title'>
            <div className='actions left-actions pt-1'>
                <ul className='ml-2'>
                    <li>
                        <h4 className='mb-0'>{communicationTemplateUpdate.selectedPartialVariable ? communicationTemplateUpdate.selectedPartialVariable.value : ''}</h4>
                    </li>
                </ul>
            </div>
            <div className='actions right-actions'>
                <ul>
                    <li>
                        <button
                            type='button'
                            className='btn btn-icon btn-close'
                            onClick={handleClose}
                        >
                            <i className='close-icon fal fa-times fs-xl'></i>
                        </button>
                    </li>
                </ul>
            </div>
        </div>
    }

    const renderContent = () => {
        return <>
            <div className='popup-body'>
                <form ref={validateRef} onSubmit={handleSubmit}>
                    <fieldset>
                        <section className={'relative p-4'} height={'75vh'}>
                            <div className='row'>
                                <div className='col-12'>
                                    <div className='form-group mb-4 validate validate-required'>
                                        <label htmlFor='email-partial-name' className='required'><small>Name</small></label>
                                        <input
                                            id='email-partial-name'
                                            type='text'
                                            className='form-control'
                                            maxLength='255'
                                            autoComplete='off'
                                            value={name ? name : ''}
                                            onChange={handleNameChange}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className='row'>
                                <div className='col-12'>
                                    <div className='form-group partial-email-body-editor mb-4 validate validate-required'>
                                        <label className='required'><small>Body</small></label>
                                        <HtmlEditor
                                            ref={emailBodyElementRef}
                                            height={300}
                                            disableTab={true}
                                            preserveFormat={true}
                                            defaultValue={
                                                communicationTemplateUpdate.selectedPartial && communicationTemplateUpdate.selectedPartial.body ?
                                                    dxm.variableToMention(communicationTemplateUpdate.selectedPartial.body, /\{\{([^}]+)\}\}/g) : null
                                            }
                                            onContentReady={handleHtmlEditorContentReady}
                                            onValueChanged={handleEmailBodyChange}
                                        >
                                            <Toolbar multiline={true}>
                                                <Item name='undo' />
                                                <Item name='redo' />
                                                <Item name='separator' />
                                                <Item name='size'
                                                    options={{ width: '90px' }}
                                                    acceptedValues={[
                                                        '8px',
                                                        '10px',
                                                        '12px',
                                                        '14px',
                                                        '16px',
                                                        '18px',
                                                        '21px',
                                                        '24px',
                                                        '28px',
                                                    ]} />
                                                <Item name='font'
                                                    acceptedValues={[
                                                        'Roboto',
                                                        'Arial',
                                                        'Courier New',
                                                        'Georgia',
                                                        'Impact',
                                                        'Lucida Console',
                                                        'Tahoma',
                                                        'Times New Roman',
                                                        'Verdana',
                                                    ]} />
                                                <Item name='separator' />
                                                <Item name='bold' />
                                                <Item name='italic' />
                                                <Item name='strike' />
                                                <Item name='underline' />
                                                <Item name='separator' />
                                                <Item name='alignLeft' />
                                                <Item name='alignCenter' />
                                                <Item name='alignRight' />
                                                <Item name='separator' />
                                                <Item name='orderedList' />
                                                <Item name='bulletList' />
                                                <Item name='separator' />
                                                <Item name='color' />
                                                <Item name='background' />
                                                <Item name='separator' />
                                                <Item name='link' />
                                                <Item
                                                    widget='dxButton'
                                                    options={{
                                                        icon: 'image',
                                                        onClick: handleUploadInlineImage,
                                                        hint: 'Upload Image to Server'
                                                    }}
                                                />
                                                <Item name='separator' />
                                                <Item name='clear' />
                                                <Item name='separator' />
                                                <Item name='insertTable' />
                                                <Item name='deleteTable' />
                                                <Item name='insertRowAbove' />
                                                <Item name='insertRowBelow' />
                                                <Item name='deleteRow' />
                                                <Item name='insertColumnLeft' />
                                                <Item name='insertColumnRight' />
                                                <Item name='deleteColumn' />
                                            </Toolbar>
                                            <Mention
                                                marker={'#'}
                                                dataSource={communicationTemplateUpdate.mentions.filter(m => !m.isPartial)}
                                                displayExpr={'value'}
                                                valueExpr={'key'}
                                                searchExpr={['searchString', 'display', 'key', 'value']}
                                                itemRender={(item) => {
                                                    return <div className='communication-template-mention-item' dangerouslySetInnerHTML={{ __html: item.display }}></div>;
                                                }}
                                            />
                                        </HtmlEditor>
                                    </div>
                                </div>
                            </div>
                            <div className='row'>
                                <div className='col-12 text-right'>
                                    {
                                        communicationTemplateUpdate.selectedPartial.isNew === true ?
                                            <button type='button' className='btn btn-cancel mr-2' onClick={handleClose}>Cancel</button> :
                                            <button type='button' className='btn btn-danger mr-2' onClick={handleDeletePartial}>Delete</button>
                                    }
                                    <button type='submit' className='btn btn-success'>Save</button>
                                </div>
                            </div>
                        </section>
                    </fieldset>
                </form>
            </div>
        </>
    }

    return <>
        <Observer>{() =>
            <>
                {
                    communicationTemplateUpdate.selectedPartial ?
                        <>
                            {renderTitle()}
                            {renderContent()}
                        </> :
                        <>
                            <div className='popup-title'></div>
                            <div className='popup-body'></div>
                        </>
                }
                <BodyEnd>
                    <ConfirmModal ref={confirmModalRef} />
                </BodyEnd>
            </>
        }</Observer>
    </>
}

export default EmailPartial;